import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useEffect, useState } from "react";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import LoadingInner from "@/components/Loading";
import NoData from "@/components/NoData";
import { useLiveData, useLiveDataForMatch } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { useMatchData } from "@/hooks/useMatchData";
import soccerBallIcon from "@/assets/images/v21-soccer-ball.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const COLLAPSED_COUNT = 10;

const ResultRow = ({ match }) => {
  const { getScore } = useLiveDataForMatch(match.id);
  const liveScore = getScore();
  const { currentHomeScore, currentAwayScore, isHomeWin, isAwayWin } = useMatchData(liveScore, match);

  return (
    <div className="grid grid-cols-[32px_minmax(0,1fr)_54px_minmax(0,1fr)] items-center gap-2 px-3 py-2 border-b border-[var(--theme-border)] last:border-b-0">
      <span className="text-xs font-semibold text-[var(--theme-subtext)] text-center">FT</span>
      <div className="flex items-center gap-1.5 min-w-0 justify-end">
        <span
          className={`text-sm truncate ${isHomeWin ? "font-semibold text-[var(--theme-text)]" : "text-[var(--theme-text)]"}`}
          title={match.homeName}
        >
          {match.homeName}
        </span>
        {match.homeTeam?.logo && (
          <img
            src={match.homeTeam.logo}
            alt={match.homeName}
            className="w-4 h-4 rounded-full object-cover shrink-0"
            loading="lazy"
          />
        )}
      </div>
      <div className="text-center text-base font-bold tabular-nums text-red-500 whitespace-nowrap">
        {currentHomeScore} - {currentAwayScore}
      </div>
      <div className="flex items-center gap-1.5 min-w-0">
        {match.awayTeam?.logo && (
          <img
            src={match.awayTeam.logo}
            alt={match.awayName}
            className="w-4 h-4 rounded-full object-cover shrink-0"
            loading="lazy"
          />
        )}
        <span
          className={`text-sm truncate ${isAwayWin ? "font-semibold text-[var(--theme-text)]" : "text-[var(--theme-text)]"}`}
          title={match.awayName}
        >
          {match.awayName}
        </span>
      </div>
    </div>
  );
};

const BangKeoKQ = ({ type = "all" }) => {
  const today = dayjs().tz(TZ).format("YYYY-MM-DD");
  const { clearAllData } = useLiveData();
  const [isExpanded, setIsExpanded] = useState(false);

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${today}`,
      filterOdds: -1,
      cache: false,
      status: type === "ketqua" ? "end" : "",
      page: 1,
      limit: 20,
    }),
    [today, type],
  );

  const { leaguesWithMatches, loading } = useBangKeoMatches(queryObject, false);

  const matches = useMemo(
    () => leaguesWithMatches.flatMap((leagueItem) => leagueItem.matches || []),
    [leaguesWithMatches],
  );
  const visibleMatches = useMemo(
    () => (isExpanded ? matches : matches.slice(0, COLLAPSED_COUNT)),
    [isExpanded, matches],
  );
  const canToggleExpand = matches.length > COLLAPSED_COUNT;

  const matchIds = useMemo(() => matches.map((match) => match.id), [matches]);

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  return (
    <div className="w-full max-w-full rounded-xl border border-[var(--theme-border)] bg-[var(--theme-foreground)] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-3 border-b border-[var(--theme-border)]">
        <div className="flex items-center gap-2 min-w-0">
          <img src={soccerBallIcon} alt="football" className="w-4 h-4 shrink-0" />
          <h3 className="text-base font-bold uppercase tracking-tight truncate text-[var(--theme-text)]">Kết quả mới nhất</h3>
        </div>
      </div>
      {loading && <LoadingInner />}
      {!loading && (
        <div className="w-full">
          {matches.length === 0 ? (
            <div className="p-3">
              <NoData message="Không có trận đấu nào" />
            </div>
          ) : (
            <>
              {visibleMatches.map((match) => <ResultRow key={match.id} match={match} />)}
              {canToggleExpand && (
                <div className="px-3 py-2.5 border-t border-[var(--theme-border)] bg-[var(--theme-foreground)]">
                  <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    className="w-full text-sm font-medium text-[var(--theme-subtext)] hover:text-[var(--theme-text)] transition-colors"
                  >
                    {isExpanded ? "Thu gọn" : "Mở rộng"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BangKeoKQ;